import type React from "react";
import { useState } from "react";

import { StepItem } from "../../../types/steps";
// import VenueDetails from "./steps/VenueDetails";

import apiService from "../../../service/Api";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../../../store/slice/AuthSlice";
import { UploadedFiles, VenueFormData } from "../../../types/venue";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
//import AddVenuePhotos2 from "./steps/AddVenuePhotos2";
import { checkPermission } from "../../../service/permissionGuard";
import { PERMISSIONS } from "../../../constants/permissions";
import VenueEssentials from "../add-venue2/steps/VenueEssentials";
import Power from "../add-venue2/steps/Power";
import Internet from "../add-venue2/steps/InternetDetails";
import PASystems from "../add-venue2/steps/PAsystems";
import Contacts from "../add-venue2/steps/Contacts";
import AddVenueLayout from "../add-venue2/AddVenueLayout";
import Room from "./steps/Rooms";

interface VenueResponse {
  id: number;
}

const AddVenue: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [formData, setFormData] = useState<VenueFormData>({
    venueName: "",
    venueType: "",
    addressOne: "",
    addressTwo: "",
    suburb: "",
    city: "Harare",
    capacity: "",
    rooms: [],

    contacts_ids: [],
    contacts: [],

    layouts: [{ layoutType: "", capacity: "" }],
    description: "",
    amenities: [],
    contactPersonName: "",
    contactPersonSurname: "",
    contactPersonEmail: "",
    contactPersonPhone: "",
    doesVenueHavePower: "yes",
    powerType: "",
    powerOutageRate: "",
    powerBackup: "",
    notes: "",

    website: "",
    facebook: "",
    instagram: "",
    backupPowerCapacity: "",
    router_distance_from_livestream: "",
    powerSocketType: "",
    internet_service_provider: "",
    wifi_name: "",
    wifi_password: "",
    internet_upload_speed: "",
    power_distance_from_livestream_desk: "",
    pa_system_distance_from_livestream: "",
    router_accessibility: "yes",
    has_pa_system: "yes",
    pa_system_provider: "",
    pa_system_contact_name: "",
    other_pa_system_service_provider: "",
    pa_system_contact_phone: "",
    pa_system_contact_email: "",
    uploadedFiles: {
      venue: [],
      paSystem: [],
      equipment: [],
      floorPlan: [],
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps: StepItem[] = [
    { label: "Venue Essentials" },
    { label: "Power" },
    { label: "Internet Details" },
    { label: "PA Systems" },
    { label: "Rooms" },
    { label: "Contacts" },
  ];

  const handleInputChange = (
    fieldOrEvent:
      | keyof VenueFormData
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: string } },
    value?: string,
  ) => {
    if (typeof fieldOrEvent === "string") {
      // Called as handleInputChange("field", "value")
      setFormData((prev) => ({ ...prev, [fieldOrEvent]: value }));
    } else {
      // Called as handleInputChange(event) or handleInputChange({ target: { name, value } })
      const { name, value: val } = fieldOrEvent.target;
      setFormData((prev) => ({ ...prev, [name]: val }));
    }
  };

  const [uploadedFiles] = useState<UploadedFiles>({
    venue: [],
    paSystem: [],
    equipment: [],
    floorPlan: [],
  });

  const handleFilesChange =
    (category: keyof VenueFormData["uploadedFiles"]) => (files: File[]) => {
      const validFiles = files.filter(
        (file) => file instanceof File && file.size > 0 && file.name,
      );
      setFormData((prev) => ({
        ...prev,
        uploadedFiles: {
          ...prev.uploadedFiles,
          [category]: [...(prev.uploadedFiles[category] ?? []), ...validFiles],
        },
      }));
    };

  const validateStep = (): string | null => {
    switch (activeStep) {
      case 0: // Venue Essentials
        if (!formData.venueName?.trim()) return "Venue Name is required";
        return null;

      default:
        return null;
    }
  };

  const handleNext = () => {
    const errorMessage = validateStep();

    if (errorMessage) {
      showErrorToast(errorMessage);
      return;
    }

    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    checkPermission(PERMISSIONS.VENUE_ADD);
    setIsSubmitting(true);

    try {
      // ─── 1. BUILD & SEND VENUE PAYLOAD ───────────────────────────────────────
      const venueFormData = new FormData();

      const validLayouts = formData.layouts.filter(
        (layout) => layout.layoutType.trim() !== "",
      );

      const venuePayload = {
        name: formData.venueName,
        venue_type: formData.venueType,
        address_one: formData.addressOne,
        address_two: formData.addressTwo,
        notes: formData.notes,
        website: formData.website || "",
        facebook: formData.facebook || "",
        instagram: formData.instagram || "",
        suburb: formData.suburb,
        city: formData.city,
        latitude: formData.latitude ?? null,
        longitude: formData.longitude ?? null,
        capacity: formData.capacity,
        layouts: validLayouts,
        description: formData.description || "",
        amenities: formData.amenities || [],
        contact_person_name: formData.contactPersonName,
        contact_id: formData.contact_id,
        contacts_ids: (formData.contacts ?? []).map((c) => Number(c.id)),
        contact_person_surname: formData.contactPersonSurname,
        contact_person_email: formData.contactPersonEmail,
        contact_person_phone: formData.contactPersonPhone,
        power_distance_from_livestream_desk:
          formData.power_distance_from_livestream_desk
            ? parseInt(formData.power_distance_from_livestream_desk)
            : null,
        does_venue_have_power: formData.doesVenueHavePower,
        power_type: formData.powerType,
        power_outage_rate: formData.powerOutageRate,
        power_backup: formData.powerBackup,
        backup_power_capacity: formData.backupPowerCapacity,
        router_distance_from_livestream:
          formData.router_distance_from_livestream
            ? parseInt(formData.router_distance_from_livestream)
            : null,
        power_socket_type: formData.powerSocketType,
        internet_service_provider: formData.internet_service_provider,
        wifi_name: formData.wifi_name,
        wifi_password: formData.wifi_password,
        internet_upload_speed: formData.internet_upload_speed
          ? parseFloat(formData.internet_upload_speed)
          : null,
        router_accessibility: formData.router_accessibility,
        has_pa_system: formData.has_pa_system,
        pa_system_provider: formData.pa_system_provider,
        pa_system_distance_from_livestream:
          formData.pa_system_distance_from_livestream
            ? parseInt(formData.pa_system_distance_from_livestream)
            : null,
        pa_system_contact_name: formData.pa_system_contact_name,
        other_pa_system_service_provider:
          formData.other_pa_system_service_provider,
        pa_system_contact_phone: formData.pa_system_contact_phone,
        pa_system_contact_email: formData.pa_system_contact_email,
        show_rooms: false, // rooms are created separately
        rooms: [],
      };

      venueFormData.append("data", JSON.stringify(venuePayload));

      // Attach venue-level files
      formData.uploadedFiles.venue.forEach((file, i) => {
        if (
          file instanceof File &&
          file.size > 0 &&
          file.type.startsWith("image/")
        )
          venueFormData.append(`venue_photos[${i}]`, file);
      });

      formData.uploadedFiles.paSystem.forEach((file, i) => {
        if (
          file instanceof File &&
          file.size > 0 &&
          file.type.startsWith("image/")
        )
          venueFormData.append(`pa_system_photos[${i}]`, file);
      });

      formData.uploadedFiles.equipment.forEach((file, i) => {
        if (
          file instanceof File &&
          file.size > 0 &&
          file.type.startsWith("image/")
        )
          venueFormData.append(`equipment_photos[${i}]`, file);
      });

      formData.uploadedFiles.floorPlan.forEach((file, i) => {
        if (
          file instanceof File &&
          file.size > 0 &&
          (file.type.startsWith("image/") || file.type === "application/pdf")
        )
          venueFormData.append(`floor_plan_photos[${i}]`, file);
      });

      // ─── 2. SUBMIT VENUE ─────────────────────────────────────────────────────
      const venueResponse = await apiService.post(
        "/api/venues/venue/full-create/",
        venueFormData,
        false,
      );

      const { id: venueId } = venueResponse.data as VenueResponse;

      const rooms = formData.rooms ?? [];

      if (rooms.length > 0) {
        await Promise.all(
          rooms.map(async (room) => {
            console.log("Submitting room:", room);

            // Structure layouts identically to the main venue configurations
            const formattedLayouts = (room.layouts ?? [])
              .filter((l) => l.id?.trim() !== "" && Number(l.capacity) > 0)
              .map((l) => ({
                layoutType: l.id,
                capacity: parseInt(l.capacity.toString(), 10),
              }));

            const roomPayload = {
              name: room.name,
              address_one: formData.addressOne,
              capacity: room.capacity
                ? parseInt(room.capacity.toString(), 10)
                : null,
              parent_id: venueId,
              country: "ZW",
              layouts: formattedLayouts, // Placed smoothly into payload
              internet_service_provider: room.internet_service_provider,
              wifi_name: room.wifi_name,
              wifi_password: room.wifi_password,
              // internet_upload_speed: room.internet_upload_speed,
              internet_upload_speed: room.internet_upload_speed
                ? parseFloat(room.internet_upload_speed)
                : null,
              router_accessibility: room.router_accessibility,
              router_distance_from_livestream:
                room.router_distance_from_livestream
                  ? parseInt(
                      room.router_distance_from_livestream.toString(),
                      10,
                    )
                  : null,
            };

            const roomFormData = new FormData();
            roomFormData.append("data", JSON.stringify(roomPayload));

            (room.photos ?? []).forEach((file, i) => {
              if (file instanceof File && file.size > 0)
                roomFormData.append(`venue_photos[${i}]`, file);
            });
            (room.floorplans ?? []).forEach((file, i) => {
              if (file instanceof File && file.size > 0)
                roomFormData.append(`floor_plan_photos[${i}]`, file);
            });

            return apiService.post(
              "/api/venues/venue/full-create/",
              roomFormData,
              false,
            );
          }),
        );
      }

      // ─── 4. SUCCESS ───────────────────────────────────────────────────────────
      showSuccessToast(
        rooms.length > 0
          ? `Venue and ${rooms.length} room(s) created successfully!`
          : "Venue created successfully!",
      );

      setTimeout(() => {
        navigate(`/venues/view/${venueId}`);
      }, 2000);
    } catch (error) {
      let errorMessage = "Failed to submit venue. Please try again.";
      const errorResponse = (
        error as {
          response?: {
            status?: number;
            data?: { detail?: string; errors?: Record<string, unknown> };
          };
        }
      )?.response;

      if (errorResponse?.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
        dispatch(logout());
        navigate("/login");
      } else if (errorResponse?.data?.detail) {
        errorMessage = errorResponse.data.detail;
        if (errorResponse.data.errors) {
          const errorDetails = Object.entries(errorResponse.data.errors)
            .map(([field, errors]) => {
              if (Array.isArray(errors))
                return `${field}: ${errors.join(", ")}`;
              if (typeof errors === "object" && errors !== null) {
                return `${field}: ${Object.entries(errors)
                  .map(([i, msgs]) =>
                    Array.isArray(msgs)
                      ? `File ${i}: ${msgs.join(", ")}`
                      : `File ${i}: ${msgs}`,
                  )
                  .join("; ")}`;
              }
              return `${field}: ${JSON.stringify(errors)}`;
            })
            .join("; ");
          errorMessage += `: ${errorDetails}`;
        }
      } else if (errorResponse?.data) {
        errorMessage = JSON.stringify(errorResponse.data);
      }

      showErrorToast(errorMessage);
      console.error("Submit error:", error, {
        responseData: errorResponse?.data,
        status: errorResponse?.status,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <VenueEssentials
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
            uploadedFiles={uploadedFiles}
            handleFilesChange={handleFilesChange}
          />
        );
      case 1:
        return (
          <Power
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
          />
        );
      case 2:
        return (
          <Internet
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
          />
        );
      case 3:
        return (
          <PASystems
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
          />
        );

      case 4:
        return (
          <Room
            rooms={formData.rooms ?? []}
            onRoomsChange={(rooms) =>
              setFormData((prev) => ({ ...prev, rooms }))
            }
          />
        );
      case 5:
        return (
          <Contacts
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
            editingContactId={editingContactId}
            setEditingContactId={setEditingContactId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AddVenueLayout
      steps={steps.map((s) => s.label)}
      activeStep={activeStep}
      formData={formData}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onSubmit={handleSubmit}
      onStepClick={(step) => {
        if (step < activeStep) {
          setActiveStep(step);
        } else if (step === activeStep + 1) {
          handleNext();
        }
      }}
      isSubmitting={isSubmitting}
      setFormData={setFormData}
      onStepChange={setActiveStep}
      editingContactId={editingContactId}
      setEditingContactId={setEditingContactId}
      onRoomsChange={(rooms) => setFormData((prev) => ({ ...prev, rooms }))}
    >
      {renderStepContent()}
    </AddVenueLayout>
  );
};

export default AddVenue;
